import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';

/**
 * Utility class for exporting 3D scenes to various formats
 * Supports GLTF, GLB, OBJ, and STL formats
 */
export class SceneExporter {
  constructor() {
    // Initialize exporters
    this.gltfExporter = new GLTFExporter();
    this.objExporter = new OBJExporter();
    this.stlExporter = new STLExporter();
  }

  /**
   * Clone and prepare scene for export by removing helper objects
   * @param {THREE.Scene} scene - The scene to prepare
   * @returns {THREE.Scene} - Cleaned scene for export
   */
  prepareSceneForExport(scene, onlyVisible = true) {
    // Ensure all world matrices are up-to-date before traversal
    scene.updateMatrixWorld(true);

    const exportScene = new THREE.Scene();

    // Copy scene properties
    if (scene.background) exportScene.background = scene.background;
    if (scene.environment) exportScene.environment = scene.environment;
    if (scene.fog) exportScene.fog = scene.fog;

    // Collect all meshes from the scene with their world transforms
    const meshesToExport = [];
    const processedObjects = new Set();

    scene.traverse((object) => {
      // Skip if already processed
      if (processedObjects.has(object)) return;

      // Handle projectors FIRST - they're lights with mesh children.
      // Use traverse (not children.forEach) so ALL descendants are marked
      // processed — otherwise body/lens/camera/stackedMeshGroup from
      // newProjector.js leak through to the general mesh handler.
      if (object.userData?.isProjector) {
        object.traverse((child) => {
          if (child === object) return;
          processedObjects.add(child);
          // Export any real mesh — covers both legacy (isProjectorMesh) and
          // new-style projectors (body/lens with projectorOverlayIgnore).
          // Exclude selection spheres and explicit helpers only.
          if (
            child.isMesh &&
            child.geometry &&
            !child.userData?.isProjectorTarget &&
            !child.userData?.isProjectorSprite &&
            !child.userData?.isHelper
          ) {
            meshesToExport.push(child);
          }
        });
        processedObjects.add(object);
        return;
      }

      // Skip measurement objects - Line2 child is isMesh=true and would leak through
      if (object.userData?.type === 'measurement' || object.userData?.isMeasurement) {
        object.traverse((child) => processedObjects.add(child));
        processedObjects.add(object);
        return;
      }

      // Skip annotation objects and annotation targets - targetSelectionSphere is isMesh=true
      // The annotation target is added directly to the scene root (not a child of the group)
      if (object.userData?.type === 'annotation' || object.userData?.isAnnotationTarget) {
        object.traverse((child) => processedObjects.add(child));
        processedObjects.add(object);
        return;
      }

      // Handle LED screens - they're meshes with panel children
      if (object.isMesh && object.userData?.type === 'led_screen') {
        // Add all panel children
        object.traverse((child) => {
          if (child !== object && child.isMesh && child.geometry) {
            meshesToExport.push(child);
            processedObjects.add(child);
          }
        });
        processedObjects.add(object);
        return;
      }

      // Handle uploaded 3D models - they're Groups containing mesh children
      if (object.userData?.type === 'model') {
        // Add all mesh children from the loaded model
        object.traverse((child) => {
          if (
            child.isMesh &&
            child.geometry &&
            child.geometry.attributes &&
            child.geometry.attributes.position &&
            !processedObjects.has(child)
          ) {
            meshesToExport.push(child);
            processedObjects.add(child);
          }
        });
        processedObjects.add(object);
        return;
      }

      // Skip non-mesh objects
      if (!object.isMesh || !object.geometry) return;

      // Skip helpers and invalid objects
      if (
        object.userData?.isHelper === true ||
        object.userData?.isProjectorTarget === true ||
        object.isTransformControlsPlane ||
        object.constructor.name === 'FatLinesBatch' ||
        object.type === 'LineSegments' ||
        object.type === 'Line'
      ) {
        return;
      }

      // Skip TransformControls gizmo meshes (visible when an object is selected)
      let ancestor = object.parent;
      while (ancestor) {
        if (ancestor.isTransformControlsGizmo || ancestor.isTransformControlsRoot) return;
        ancestor = ancestor.parent;
      }

      // Skip floor planes, infinite grid, and measurement ground plane
      if (
        object.userData?.type === 'panel' ||
        object.userData?.type === 'infiniteGrid' ||
        object.parent?.userData?.type === 'infiniteGrid' ||
        object.userData?.isMeasurementGroundPlane === true
      ) {
        return;
      }

      meshesToExport.push(object);
    });

    // Process each mesh
    meshesToExport.forEach((originalMesh) => {
      // Skip invisible meshes when onlyVisible is set
      if (onlyVisible && !originalMesh.visible) return;

      // Validate geometry has required attributes
      if (!originalMesh.geometry || !originalMesh.geometry.attributes || !originalMesh.geometry.attributes.position) {
        return;
      }

      // Clone the geometry
      const geometry = originalMesh.geometry.clone();

      // Update the original mesh's matrices to ensure they're current,
      // walking UP the parent chain first so parent world matrices are correct
      originalMesh.updateWorldMatrix(true, false);

      // Apply the mesh's world transformation to the geometry
      // This bakes position, rotation, and scale into the vertex positions
      geometry.applyMatrix4(originalMesh.matrixWorld);

      // Clone the material with color preserved and full emissive lighting
      const TEXTURE_MAPS = [
        'map',
        'emissiveMap',
        'normalMap',
        'roughnessMap',
        'metalnessMap',
        'aoMap',
        'displacementMap',
        'alphaMap',
        'envMap',
      ];

      const applyFullLighting = (cloned, source) => {
        if (source.color) cloned.color.copy(source.color);
        if ('emissive' in cloned) cloned.emissive.copy(source.color ?? new THREE.Color(0xcccccc));
        if ('emissiveIntensity' in cloned) cloned.emissiveIntensity = 1;
      };

      const stripNonSerializableTextures = (mat) => {
        for (const key of TEXTURE_MAPS) {
          if (mat[key] instanceof THREE.CanvasTexture || mat[key] instanceof THREE.VideoTexture) {
            mat[key] = null;
          }
        }
        mat.needsUpdate = true;
      };

      let material;
      if (Array.isArray(originalMesh.material)) {
        material = originalMesh.material.map((m) => {
          const cloned = m.clone();
          applyFullLighting(cloned, m);
          stripNonSerializableTextures(cloned);
          return cloned;
        });
      } else if (originalMesh.material) {
        material = originalMesh.material.clone();
        applyFullLighting(material, originalMesh.material);
        stripNonSerializableTextures(material);
      } else {
        material = new THREE.MeshStandardMaterial({ color: 0xcccccc });
        applyFullLighting(material, material);
      }

      // Create new mesh with transformed geometry at origin
      const mesh = new THREE.Mesh(geometry, material);
      const findModuleName = (obj) => {
        let current = obj;
        while (current) {
          if (current.userData?.name) return current.userData.name;
          current = current.parent;
        }
        return null;
      };
      mesh.name = findModuleName(originalMesh) || originalMesh.userData?.code || 'unnamed';

      exportScene.add(mesh);
    });

    return exportScene;
  }

  /**
   * Get only mesh objects from scene (for specific exporters that need meshes)
   * @param {THREE.Scene} scene - The scene to extract meshes from
   * @returns {THREE.Object3D} - Object containing all meshes with baked transforms
   */
  getMeshesOnly(scene) {
    // For STL, we need all meshes as a single object
    // We'll return the scene itself since prepareSceneForExport already created a clean scene
    return scene;
  }

  /**
   * Dispose of cloned resources in the export scene to prevent memory leaks
   * @param {THREE.Scene} exportScene - The temporary export scene to clean up
   */
  disposeExportScene(exportScene) {
    if (!exportScene) return;

    // Traverse and dispose all meshes, geometries, and materials
    exportScene.traverse((object) => {
      if (object.isMesh) {
        // Dispose geometry
        if (object.geometry) {
          object.geometry.dispose();
        }

        // Dispose material(s)
        if (object.material) {
          if (Array.isArray(object.material)) {
            // Handle material array
            object.material.forEach((material) => {
              if (material && material.dispose) {
                material.dispose();
              }
            });
          } else {
            // Handle single material
            if (object.material.dispose) {
              object.material.dispose();
            }
          }
        }
      }
    });

    // Clear the scene
    while (exportScene.children.length > 0) {
      exportScene.remove(exportScene.children[0]);
    }
  }

  /**
   * Export scene to GLTF format
   * @param {THREE.Scene} scene - Scene to export
   * @param {Object} options - Export options
   * @returns {Promise<Blob>}
   */
  async exportGLTF(scene, options = {}) {
    return new Promise((resolve, reject) => {
      const onlyVisible = options.onlyVisible !== undefined ? options.onlyVisible : true;
      const cleanScene = this.prepareSceneForExport(scene, onlyVisible);

      const exportOptions = {
        binary: options.binary || false,
        trs: options.trs !== undefined ? options.trs : false,
        onlyVisible,
        truncateDrawRange: true,
        embedImages: true,
        maxTextureSize: options.maxTextureSize || 4096,
        ...options,
      };

      this.gltfExporter.parse(
        cleanScene,
        (result) => {
          try {
            if (exportOptions.binary) {
              // GLB format
              const blob = new Blob([result], {
                type: 'application/octet-stream',
              });
              resolve(blob);
            } else {
              // GLTF format
              const json = JSON.stringify(result, null, 2);
              const blob = new Blob([json], { type: 'application/json' });
              resolve(blob);
            }
          } finally {
            // Dispose cloned resources to prevent memory leaks
            this.disposeExportScene(cleanScene);
          }
        },
        (error) => {
          // Dispose cloned resources even on error
          this.disposeExportScene(cleanScene);
          reject(error);
        },
        exportOptions
      );
    });
  }

  /**
   * Export scene to OBJ format
   * @param {THREE.Scene} scene - Scene to export
   * @param {Object} options - Export options
   * @returns {Promise<Blob>}
   */
  async exportOBJ(scene, options = {}) {
    return new Promise((resolve, reject) => {
      const onlyVisible = options.onlyVisible !== undefined ? options.onlyVisible : true;
      let cleanScene = null;
      try {
        cleanScene = this.prepareSceneForExport(scene, onlyVisible);
        const result = this.objExporter.parse(cleanScene);
        const blob = new Blob([result], { type: 'text/plain' });
        resolve(blob);
      } catch (error) {
        console.error('OBJ Export error:', error);
        reject(error);
      } finally {
        this.disposeExportScene(cleanScene);
      }
    });
  }

  /**
   * Export scene to STL format
   * @param {THREE.Scene} scene - Scene to export
   * @param {Object} options - Export options
   * @returns {Promise<Blob>}
   */
  async exportSTL(scene, options = {}) {
    return new Promise((resolve, reject) => {
      const onlyVisible = options.onlyVisible !== undefined ? options.onlyVisible : true;
      let cleanScene = null;
      try {
        cleanScene = this.prepareSceneForExport(scene, onlyVisible);
        const meshes = this.getMeshesOnly(cleanScene);
        const exportOptions = { binary: options.binary !== undefined ? options.binary : true };
        const result = this.stlExporter.parse(meshes, exportOptions);
        const blob = exportOptions.binary
          ? new Blob([result], { type: 'application/octet-stream' })
          : new Blob([result], { type: 'text/plain' });
        resolve(blob);
      } catch (error) {
        console.error('STL Export error:', error);
        reject(error);
      } finally {
        this.disposeExportScene(cleanScene);
      }
    });
  }

  /**
   * Export scene based on format type
   * @param {THREE.Scene} scene - Scene to export
   * @param {string} format - Export format (gltf, glb, obj, stl)
   * @param {Object} options - Format-specific export options
   * @returns {Promise<Blob>}
   */
  async export(scene, format, options = {}) {
    const formatLower = format.toLowerCase();

    switch (formatLower) {
      case 'gltf':
        return this.exportGLTF(scene, { ...options, binary: false });
      case 'glb':
        return this.exportGLTF(scene, { ...options, binary: true });
      case 'obj':
        return this.exportOBJ(scene, options);
      case 'stl':
        return this.exportSTL(scene, options);
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  /**
   * Download exported scene as a file
   * @param {Blob} blob - The blob to download
   * @param {string} filename - Desired filename with extension
   */
  downloadFile(blob, filename) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    requestAnimationFrame(() => URL.revokeObjectURL(link.href));
  }

  /**
   * Export and download scene in one step
   * @param {THREE.Scene} scene - Scene to export
   * @param {string} format - Export format
   * @param {string} filename - Filename without extension
   * @param {Object} options - Export options
   * @returns {Promise<void>}
   */
  async exportAndDownload(scene, format, filename, options = {}) {
    try {
      const blob = await this.export(scene, format, options);
      const extension = format.toLowerCase();
      const fullFilename = `${filename}.${extension}`;
      this.downloadFile(blob, fullFilename);
    } catch (error) {
      console.error(`Error exporting scene as ${format}:`, error);
      throw error;
    }
  }

  /**
   * Get file extension for format
   * @param {string} format - Export format
   * @returns {string} - File extension
   */
  static getExtension(format) {
    const extensions = {
      gltf: 'gltf',
      glb: 'glb',
      obj: 'obj',
      stl: 'stl',
    };
    return extensions[format.toLowerCase()] || format.toLowerCase();
  }

  /**
   * Get MIME type for format
   * @param {string} format - Export format
   * @returns {string} - MIME type
   */
  static getMimeType(format) {
    const mimeTypes = {
      gltf: 'model/gltf+json',
      glb: 'model/gltf-binary',
      obj: 'text/plain',
      stl: 'model/stl',
    };
    return mimeTypes[format.toLowerCase()] || 'application/octet-stream';
  }
}

// Export singleton instance for convenience
export const sceneExporter = new SceneExporter();
